/**
 * Retirement Wealth Trajectory
 *
 * WP-RETIRE-12: Shapes year-by-year projected wealth for charting, so a user
 * can see the effect of a change as a curve rather than infer it from a grid
 * of coloured cells.
 *
 * Reuses calculateProjectedWealth — the SAME function behind the projection
 * grid — so the chart can never disagree with the numbers beside it. Kept as
 * its own module (one concern per file) and pure, so it is testable without
 * rendering anything.
 *
 * All money is integer cents.
 */
import {
  LIFE_EXPECTANCY,
  RETURN_RATES,
  calculateProjectedWealth,
} from './retirementCalculations';
import { formatCurrencyCompact } from './format';
import type {
  RetirementPlanningInputs,
  RetirementEvent,
} from '@/types/retirement';

/** One age on the x-axis, with a wealth value per return-rate scenario. */
export interface TrajectoryPoint {
  age: number;
  /** True while the user is still accumulating (age < retirementAge). */
  isAccumulating: boolean;
  /** Projected wealth in cents, keyed by return rate as a string. */
  [seriesKey: string]: number | boolean;
}

/**
 * Format a projected net worth value for an axis tick or a tooltip.
 *
 * formatCurrencyCompact takes CENTS and divides by 100 itself (utils/format.ts),
 * which is what every other caller passes it (HeatmapCell,
 * RetirementSummaryLine). TrajectoryChart converted to dollars FIRST, so every
 * label was 100x too small: $1.2M of net worth rendered as "$12K", which reads
 * as one year's change rather than a balance. The plotted values were always
 * correct - only the labels were wrong.
 *
 * Lives here rather than in the chart so the unit contract is testable without
 * rendering recharts into jsdom, where a zero-size container produces no ticks.
 */
export function formatNetWorthCents(cents: number): string {
  return formatCurrencyCompact(cents);
}

/** Stable series key for a return rate, e.g. 0.05 -> "rate_5". */
export function seriesKeyForRate(rate: number): string {
  return `rate_${Math.round(rate * 100)}`;
}

/**
 * Build the wealth trajectory from the user's current age to life expectancy.
 *
 * One series per entry in RETURN_RATES — exactly the three scenarios the
 * product already shows. This deliberately does not add a continuous risk
 * control: the three-scenario model is a simplicity the user asked to keep.
 */
export function buildTrajectorySeries(
  inputs: RetirementPlanningInputs,
  netWorthCents: number,
  events?: RetirementEvent[],
  birthYear?: number
): TrajectoryPoint[] {
  const points: TrajectoryPoint[] = [];

  const retirementIncomeCents =
    inputs.socialSecurityAnnualCents +
    inputs.pensionAnnualCents +
    inputs.otherRetirementIncomeCents;

  const incomeSources = {
    socialSecurityAnnualCents: inputs.socialSecurityAnnualCents,
    pensionAnnualCents: inputs.pensionAnnualCents,
    otherRetirementIncomeCents: inputs.otherRetirementIncomeCents,
    socialSecurityStartAge: inputs.socialSecurityStartAge,
    pensionStartAge: inputs.pensionStartAge,
    otherIncomeStartAge: inputs.otherIncomeStartAge,
  };

  for (let age = inputs.currentAge; age <= LIFE_EXPECTANCY; age += 1) {
    const point: TrajectoryPoint = {
      age,
      isAccumulating: age < inputs.retirementAge,
    };

    for (const rate of RETURN_RATES) {
      point[seriesKeyForRate(rate)] = calculateProjectedWealth(
        inputs.currentAge,
        inputs.retirementAge,
        age,
        netWorthCents,
        inputs.currentSavingsAnnualCents,
        retirementIncomeCents,
        inputs.retirementExpensesAnnualCents,
        rate,
        events,
        birthYear,
        incomeSources
      );
    }

    points.push(point);
  }

  return points;
}

/**
 * Age at which wealth first reaches zero for a given scenario, or null if it
 * never does. Surfacing the depletion point is the single most useful thing a
 * trajectory chart can tell someone.
 */
export function depletionAge(
  points: TrajectoryPoint[],
  rate: number
): number | null {
  const key = seriesKeyForRate(rate);
  for (const point of points) {
    const value = point[key];
    if (typeof value === 'number' && value <= 0) {
      return point.age;
    }
  }
  return null;
}
